import { Group, Space, Tooltip } from "@mantine/core";
import { IconAlertTriangleFilled } from "@tabler/icons-react";
import { RoundedNumberFormat } from "../../../../utils/RoundedNumberFormat";
import "./MantineRoundedNumberCell.scss";

interface MantineRoundedNumberCellProps {
  decimalScale?: number;
  unit?: string;
  value: number;
  fixedDecimalScale?: boolean;
  warningMessage?: string;
  extraSpaceRight?: boolean;
}

/**
 * Mantine React Table helper component to render a cell with formatted and rounded number, plus optional warning icon and tooltip
 *
 * @param {MantineRoundedNumberCellProps} props
 * @param {number} [props.decimalScale=2] - Number of digits after decimal separator
 * @param {string} [props.unit] - Unit to write as suffix after number
 * @param {number} props.value - Numeric value to render
 * @param {boolean} [props.fixedDecimalScale=true] - Whether to pad trailing zeroes to match `decimalScale` length if needed
 * @param {string} [props.warningMessage] - If set, renders warning icon which displays this message as tooltip on mouseover
 * @param {boolean} [props.extraSpaceRight] - Enabling this creates an extra space the size of `ActionIcon` on the right side, to keep layout consistency in tables where some cells have context menus
 */
function MantineRoundedNumberCell({
  decimalScale = 2,
  unit,
  value,
  fixedDecimalScale = true,
  warningMessage,
  extraSpaceRight
}: MantineRoundedNumberCellProps) {
  return (
    <Group
      className="MantineRoundedNumberCell"
      gap="sm"
      justify={warningMessage ? "space-between" : "flex-end"}
      wrap="nowrap"
    >
      {warningMessage && (
        <Tooltip label={warningMessage} multiline position="left" w={220}>
          <IconAlertTriangleFilled
            color="var(--mantine-color-warning-filled)"
            role="img"
            size={20}
            title="Warnhinweis zu diesem Wert"
          />
        </Tooltip>
      )}
      <Group gap={0} wrap="nowrap">
        <RoundedNumberFormat
          decimalScale={decimalScale}
          decimalSeparator=","
          displayType="text"
          fixedDecimalScale={fixedDecimalScale}
          suffix={unit}
          thousandSeparator="."
          value={value}
        />
        {extraSpaceRight && <Space h={28} w={28} />}
      </Group>
    </Group>
  );
}

export { MantineRoundedNumberCell, MantineRoundedNumberCellProps };
